import { Flag } from 'lucide-react';
import { getStatusOption, type TabStatusField } from '../config/tabStatus';
import { cn } from '@/lib/utils';
import SplitChip from '@/components/SplitChip';

/**
 * Bucket key for a row's status value in a tab's status field: the matching
 * option's value, or '' (No Status) for blank/unknown values. Shared by the
 * chips and the row filter so they can never disagree.
 */
export function statusKeyOf(field: TabStatusField, status: unknown): string {
  return getStatusOption(field, status)?.value ?? '';
}

/** A simple on/off filter chip (Ready To Book, Ready For Taxes, AMA flag) */
interface ToggleChip {
  /** Rows matching the chip's filter */
  count: number;
  active: boolean;
  onToggle: () => void;
  /** Tooltip explaining what qualifies */
  title?: string;
}

/** The client service flags the Services chip filters by — column display
 *  names, which double as row_data keys (loader-overlaid from clients) */
export const SERVICE_FILTER_COLUMNS = [
  'Sales Tax',
  'Payroll',
  'Bookkeeping',
  '1099',
  'S Corp Election',
] as const;

interface ServicesChip {
  /** Rows with each service flag on (menu counts) */
  counts: Map<string, number>;
  /** Selected service column names; empty = no filtering */
  selected: Set<string>;
  /** Total rows — the "All" menu row's count */
  total: number;
  onToggle: (column: string) => void;
  onClear: () => void;
  /** Body toggle when inactive: select every service (= "has any service") */
  onSelectAll: () => void;
  title?: string;
}

/** Which booked stamps the "Booked" chip matches against */
export type BookedMode = 'qb' | 'manual' | 'both';

const BOOKED_MODES: BookedMode[] = ['qb', 'manual', 'both'];

const BOOKED_MODE_LABELS: Record<BookedMode, string> = {
  qb: 'QB Statements',
  manual: 'Manual Receipts',
  both: 'Both',
};

/** Short mode suffix for the chip itself ('both' shows no suffix) */
const BOOKED_MODE_SUFFIXES: Record<BookedMode, string> = {
  qb: ': QB',
  manual: ': Receipts',
  both: '',
};

interface BookedChip {
  /** Rows booked for the accounting month, per mode (shown in the menu) */
  counts: Record<BookedMode, number>;
  mode: BookedMode;
  active: boolean;
  onToggle: () => void;
  /** Selecting a mode also activates the filter */
  onModeChange: (mode: BookedMode) => void;
  /** Tooltip explaining which month qualifies */
  title?: string;
}

/** QB Process buckets: none = blank or legacy non-Manual/Online values */
export type QbProcessMode = 'manual' | 'online' | 'none';

const QB_PROCESS_MODES: QbProcessMode[] = ['manual', 'online', 'none'];

export const QB_PROCESS_MODE_LABELS: Record<QbProcessMode, string> = {
  manual: 'Manual',
  online: 'Online',
  none: 'None',
};

interface QbProcessChip {
  /** Rows per QB Process bucket (shown in the menu) */
  counts: Record<QbProcessMode, number>;
  /** Total rows — the "All" menu row's count */
  total: number;
  mode: QbProcessMode;
  active: boolean;
  onToggle: () => void;
  /** Selecting a mode activates the filter; 'all' clears it */
  onModeChange: (mode: QbProcessMode | 'all') => void;
  title?: string;
}

interface StatusFilterChipsProps {
  /** The active tab's status field; absent = the tab has no status chip */
  statusField?: TabStatusField;
  /** Row count per status key — statuses with no rows are hidden */
  counts: Map<string, number>;
  /** Selected status keys; empty set = show all */
  selected: Set<string>;
  onToggle: (statusKey: string) => void;
  onClear: () => void;
  /** "Services" split chip rendered after Account Status (client flags) */
  services?: ServicesChip;
  /** Trailing "Ready To Book" chip — independent of the status filter */
  readyToBook?: ToggleChip;
  /** Trailing "Ready For Taxes" chip (the boolean bookkeeping column) */
  readyForTaxes?: ToggleChip;
  /** Trailing "Booked" split chip — independent of the status filter */
  booked?: BookedChip;
  /** Trailing "QB Process" split chip — independent of the status filter */
  qbProcess?: QbProcessChip;
  /** Trailing Ask My Accountant flag chip (the column's red flag icon) */
  askMyAccountant?: ToggleChip;
  /** Extra chips rendered after Account Status (the assignee split chips) */
  children?: React.ReactNode;
  /** Shows the trailing "Clear all" link */
  hasActiveFilters?: boolean;
  /** Clears every filter (statuses, assignees, toggles, search) */
  onClearAll?: () => void;
}

export default function StatusFilterChips({
  statusField,
  counts,
  selected,
  onToggle,
  onClear,
  services,
  readyToBook,
  readyForTaxes,
  booked,
  qbProcess,
  askMyAccountant,
  children,
  hasActiveFilters,
  onClearAll,
}: StatusFilterChipsProps) {
  const present = (statusField?.options ?? []).filter(
    o => (counts.get(o.value) ?? 0) > 0
  );
  // With 0–1 statuses the status chip filters nothing — show only the
  // trailing filter chips (or nothing at all)
  const showStatus = present.length > 1;
  const leading = showStatus || services;
  const trailing =
    readyToBook || readyForTaxes || booked || qbProcess || askMyAccountant;
  if (!leading && !trailing && !children) return null;

  let servicesLabel = 'Services';
  if (services) {
    const picked = SERVICE_FILTER_COLUMNS.filter(c => services.selected.has(c));
    if (picked.length === 1) servicesLabel = `Services: ${picked[0]}`;
    else if (picked.length > 1) {
      servicesLabel = `Services: ${picked.length} selected`;
    }
  }

  const total = [...counts.values()].reduce((sum, n) => sum + n, 0);
  const selectedOptions = present.filter(o => selected.has(o.value));
  const statusName = statusField?.column ?? 'Status';
  let statusLabel = statusName;
  if (selectedOptions.length === 1) {
    statusLabel = `${statusName}: ${selectedOptions[0].label}`;
  } else if (selectedOptions.length > 1) {
    statusLabel = `${statusName}: ${selectedOptions.length} selected`;
  }

  return (
    <div
      className="flex items-center gap-1.5 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]"
      role="group"
      aria-label="Filter accounts"
    >
      {showStatus && (
        <SplitChip
          label={statusLabel}
          active={selected.size > 0}
          // Main segment = the old "All" chip: clear back to every status
          onToggle={onClear}
          title={`Only rows in the checked ${statusName} values`}
          menuAriaLabel={`Choose ${statusName.toLowerCase()} values`}
          menu={{
            type: 'checkbox',
            checked: selected,
            allItem: { label: 'All', count: total, onSelect: onClear },
            items: present.map(option => ({
              value: option.value,
              label: (
                <span className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className="h-2.5 w-2.5 shrink-0 rounded-full border border-black/10"
                    style={{ backgroundColor: option.highlightColor }}
                  />
                  {option.label}
                </span>
              ),
              count: counts.get(option.value),
            })),
            onChange: value => onToggle(value),
          }}
        />
      )}
      {services && (
        <SplitChip
          label={servicesLabel}
          active={services.selected.size > 0}
          // Body when inactive = "has any service" (all five checked)
          onToggle={
            services.selected.size > 0 ? services.onClear : services.onSelectAll
          }
          title={services.title}
          menuAriaLabel="Choose services"
          menu={{
            type: 'checkbox',
            checked: services.selected,
            allItem: {
              label: 'All',
              count: services.total,
              onSelect: services.onClear,
            },
            items: SERVICE_FILTER_COLUMNS.map(column => ({
              value: column,
              label: column,
              count: services.counts.get(column) ?? 0,
            })),
            onChange: value => services.onToggle(value),
          }}
        />
      )}
      {/* Group divider: [Account Status + Services] | [assignees] | [progress] */}
      {leading && children && (
        <div aria-hidden className="mx-1 h-4 w-px shrink-0 bg-border" />
      )}
      {children}
      {(leading || children) && trailing && (
        <div aria-hidden className="mx-1 h-4 w-px shrink-0 bg-border" />
      )}
      {readyToBook && (
        <button
          onClick={readyToBook.onToggle}
          aria-pressed={readyToBook.active}
          title={readyToBook.title}
          className={cn(
            'shrink-0 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
            readyToBook.active
              ? 'border-transparent bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
              : 'border-border text-muted-foreground hover:text-foreground'
          )}
        >
          Ready To Book · {readyToBook.count}
        </button>
      )}
      {booked && (
        <SplitChip
          label={`Booked${BOOKED_MODE_SUFFIXES[booked.mode]} · ${booked.counts[booked.mode]}`}
          active={booked.active}
          onToggle={booked.onToggle}
          title={booked.title}
          menuAriaLabel="Choose which booked stamps count"
          menu={{
            type: 'radio',
            value: booked.mode,
            items: BOOKED_MODES.map(mode => ({
              value: mode,
              label: BOOKED_MODE_LABELS[mode],
              count: booked.counts[mode],
            })),
            onChange: v => booked.onModeChange(v as BookedMode),
          }}
        />
      )}
      {qbProcess && (
        <SplitChip
          // Inactive: just the count of accounts WITH QB Process data;
          // active: the selected bucket and its count
          label={
            qbProcess.active
              ? `QB Process: ${QB_PROCESS_MODE_LABELS[qbProcess.mode]} · ${qbProcess.counts[qbProcess.mode]}`
              : `QB Process · ${qbProcess.counts.manual + qbProcess.counts.online}`
          }
          active={qbProcess.active}
          onToggle={qbProcess.onToggle}
          title={qbProcess.title}
          menuAriaLabel="Choose which QB Process values count"
          menu={{
            type: 'radio',
            value: qbProcess.active ? qbProcess.mode : 'all',
            items: [
              ...QB_PROCESS_MODES.map(mode => ({
                value: mode,
                label: QB_PROCESS_MODE_LABELS[mode],
                count: qbProcess.counts[mode],
              })),
              { value: 'all', label: 'All', count: qbProcess.total },
            ],
            onChange: v => qbProcess.onModeChange(v as QbProcessMode | 'all'),
          }}
        />
      )}
      {readyForTaxes && (
        <button
          onClick={readyForTaxes.onToggle}
          aria-pressed={readyForTaxes.active}
          title={readyForTaxes.title}
          className={cn(
            'shrink-0 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
            readyForTaxes.active
              ? 'border-transparent bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
              : 'border-border text-muted-foreground hover:text-foreground'
          )}
        >
          Ready For Taxes · {readyForTaxes.count}
        </button>
      )}
      {askMyAccountant && (
        <button
          onClick={askMyAccountant.onToggle}
          aria-pressed={askMyAccountant.active}
          aria-label="Filter to Ask My Accountant flagged accounts"
          title={askMyAccountant.title}
          className={cn(
            'inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
            askMyAccountant.active
              ? 'border-transparent bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
              : 'border-border text-muted-foreground hover:text-foreground'
          )}
        >
          <Flag className="h-3 w-3 fill-red-600 text-red-600" />
          {askMyAccountant.count}
        </button>
      )}
      {hasActiveFilters && onClearAll && (
        <button
          type="button"
          onClick={onClearAll}
          className="shrink-0 whitespace-nowrap text-xs font-medium text-primary hover:underline"
        >
          Clear all
        </button>
      )}
    </div>
  );
}
